export function normalizeTablesHtml(html: string, maxWidth: number): string {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const getPx = (value?: string | null) => {
    if (!value) return 0;
    const match = value.match(/width\s*:\s*([0-9.]+)px/i);
    return match ? Number(match[1]) : 0;
  };

  const setWidthStyle = (el: Element, px: number) => {
    const current = el.getAttribute("style") || "";
    const withoutWidth = current
      .replace(/(?:^|;)\s*width\s*:\s*[^;]+;?/gi, "")
      .replace(/^\s*;\s*|\s*;\s*$/g, "")
      .trim();

    const next = `${withoutWidth ? withoutWidth + "; " : ""}width: ${px}px;`;
    el.setAttribute("style", next);
  };

  doc.querySelectorAll("table").forEach((table) => {
    const colEls = Array.from(table.querySelectorAll("col"));
    if (!colEls.length) return;

    let widths = colEls.map((col) => getPx(col.getAttribute("style")));

    if (widths.some((w) => !w)) {
      const firstRowCells = Array.from(
        table.querySelectorAll("tr:first-child td, tr:first-child th"),
      );
      if (firstRowCells.length) {
        const rebuilt: number[] = [];
        firstRowCells.forEach((cell) => {
          const colwidthAttr = cell.getAttribute("colwidth");
          const colspan = Number(cell.getAttribute("colspan") || "1");
          if (colwidthAttr) {
            const parts = colwidthAttr
              .split(",")
              .map((n) => Number(n.trim()))
              .filter((n) => Number.isFinite(n) && n > 0);

            if (parts.length === colspan) {
              rebuilt.push(...parts);
            } else if (parts.length === 1 && colspan > 1) {
              for (let i = 0; i < colspan; i++) rebuilt.push(parts[0]);
            }
          }
        });

        if (rebuilt.length === colEls.length) {
          widths = rebuilt;
        }
      }
    }

    const total = widths.reduce((a, b) => a + b, 0);
    if (!total) return;

    let finalWidths = widths;
    if (total > maxWidth) {
      const scale = maxWidth / total;
      finalWidths = widths.map((w) => Math.max(24, Math.floor(w * scale)));

      let sum = finalWidths.reduce((a, b) => a + b, 0);
      if (sum !== maxWidth && finalWidths.length > 0) {
        finalWidths[finalWidths.length - 1] += maxWidth - sum;
      }
    }
    setWidthStyle(
      table,
      Math.min(
        maxWidth,
        finalWidths.reduce((a, b) => a + b, 0),
      ),
    );

    colEls.forEach((col, i) => {
      setWidthStyle(col, finalWidths[i] || 24);
    });

    let rowForMapping = table.querySelector("tr");
    while (rowForMapping) {
      let colIndex = 0;
      const cells = Array.from(rowForMapping.children).filter(
        (el) => el.tagName === "TD" || el.tagName === "TH",
      );

      cells.forEach((cell) => {
        const colspan = Number(cell.getAttribute("colspan") || "1");
        const slice = finalWidths.slice(colIndex, colIndex + colspan);

        if (slice.length) {
          cell.setAttribute("colwidth", slice.join(","));
        }

        colIndex += colspan;
      });

      rowForMapping =
        rowForMapping.nextElementSibling as HTMLTableRowElement | null;
    }
  });

  return doc.body.innerHTML;
}
